import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Send, MessageSquare } from "lucide-react";

export default function Messages() {
  const { user } = useAuth();
  const [recipients, setRecipients] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  const loadContacts = () => api.get("/messages/contacts").then(({ data }) => setRecipients(data));
  useEffect(() => { loadContacts(); }, []);

  // Deep link from the new-message banner: /messages?with=<user id> opens that conversation.
  useEffect(() => {
    const withId = new URLSearchParams(window.location.search).get("with");
    if (!withId || active || recipients.length === 0) return;
    const r = recipients.find((x) => x.id === withId);
    if (r) loadConv(r);
  }, [recipients]);

  const loadConv = (r) => {
    setActive(r);
    api.get(`/messages?with_user=${r.id}`).then(({ data }) => { setMsgs(data); loadContacts(); });
  };
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => api.get(`/messages?with_user=${active.id}`).then(({ data }) => setMsgs(data)), 8000);
    return () => clearInterval(t);
  }, [active]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if (!text.trim() || !active) return;
    try {
      await api.post("/messages", { recipient_user_id: active.id, message_body: text });
      setText(""); loadConv(active);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const grouped = recipients.reduce((acc, r) => { (acc[r.role] = acc[r.role] || []).push(r); return acc; }, {});

  return (
    <div>
      <PageHeader title="Messages" subtitle="You can only message people connected to your placements" />
      <div className="grid md:grid-cols-3 gap-4" style={{ minHeight: "60vh" }}>
        <Card className="p-0 md:col-span-1 overflow-hidden">
          <div className="max-h-[65vh] overflow-y-auto cc-scroll">
            {recipients.length === 0 ? <EmptyState text="No one to message yet." /> :
              Object.entries(grouped).map(([role, people]) => (
                <div key={role}>
                  <div className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-400 bg-zinc-50 sticky top-0">{role}</div>
                  {people.map((r) => (
                    <button key={r.id} onClick={() => loadConv(r)} data-testid={`recipient-${r.id}`}
                      className={`w-full text-left px-4 py-3 border-b hover:bg-zinc-50 flex items-center justify-between gap-2 ${active?.id === r.id ? "bg-cc-blue/5" : ""}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-zinc-800 truncate">{r.name}</p>
                        <p className="text-xs text-zinc-400 truncate">{r.email}</p>
                        {(r.school_name || r.program) && <p className="text-xs text-cc-blue truncate" data-testid={`recipient-meta-${r.id}`}>{[r.school_name, r.program].filter(Boolean).join(" · ")}</p>}
                      </div>
                      {r.unread > 0 && <span className="bg-cc-orange text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center shrink-0" data-testid={`unread-${r.id}`}>{r.unread}</span>}
                    </button>
                  ))}
                </div>
              ))}
          </div>
        </Card>

        <Card className="p-0 md:col-span-2 flex flex-col">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-zinc-400 flex-col gap-2">
              <MessageSquare className="w-8 h-8" /> Select someone to start a conversation
            </div>
          ) : (
            <>
              <div className="px-5 py-3 border-b">
                <p className="font-medium text-zinc-800">{active.name}</p>
                <p className="text-xs text-zinc-400">{active.role}{(active.school_name || active.program) ? ` · ${[active.school_name, active.program].filter(Boolean).join(" · ")}` : ""}</p>
              </div>
              <div className="flex-1 p-5 space-y-3 overflow-y-auto cc-scroll max-h-[50vh]">
                {msgs.length === 0 ? <EmptyState text="No messages yet. Say hello!" /> :
                  msgs.map((m) => (
                    <div key={m.id} className={`flex ${m.sender_user_id === user.id ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${m.sender_user_id === user.id ? "bg-cc-blue text-white" : "bg-zinc-100"}`}>
                        {m.message_body}
                        <div className="text-[10px] opacity-60 mt-1">{new Date(m.created_at).toLocaleString()}</div>
                      </div>
                    </div>
                  ))}
                <div ref={endRef} />
              </div>
              <div className="p-4 border-t flex gap-2">
                <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" onKeyDown={(e) => e.key === "Enter" && send()} data-testid="message-input" />
                <Button onClick={send} className="bg-cc-blue text-white" data-testid="send-message-btn"><Send className="w-4 h-4" /></Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
